import { useState } from "react";
import { Plus, MoreHorizontal, Archive, Pencil, RotateCcw } from "lucide-react";
import { SettingsPage } from "@/components/v2/settings-page";
import { SettingsTable } from "@/components/v2/settings-table";
import { BusinessFormSheet } from "@/components/v2/business-form-sheet";
import { ConfirmDialog } from "@/components/v2/confirm-dialog";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  useAccountBusinesses,
  useUpdateBusiness,
} from "@/hooks/use-account-businesses";
import { useAbility } from "@/hooks/use-ability";
import { BusinessDataActionItems } from "@/components/business-data-actions";
import type { Business } from "@adva/types";

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString();
}

// ─── Actions Menu ────────────────────────────────────────────────────────────

function ActionsMenu({
  business,
  onEdit,
  onArchive,
  onReactivate,
}: {
  business: Business;
  onEdit: (b: Business) => void;
  onArchive: (b: Business) => void;
  onReactivate: (b: Business) => void;
}) {
  const isInactive = business.status === "inactive";
  const ability = useAbility();
  const showBackupActions = !isInactive && ability.can("reset", "Business");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {showBackupActions && <BusinessDataActionItems business={business} />}
        <DropdownMenuItem onClick={() => onEdit(business)}>
          <Pencil className="mr-2 h-3.5 w-3.5" />
          Edit
        </DropdownMenuItem>
        {isInactive ? (
          <DropdownMenuItem onClick={() => onReactivate(business)}>
            <RotateCcw className="mr-2 h-3.5 w-3.5" />
            Reactivate
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => onArchive(business)}
          >
            <Archive className="mr-2 h-3.5 w-3.5" />
            Archive
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function V2SettingsBusinessesPage() {
  const { data, isLoading } = useAccountBusinesses();
  const updateBusiness = useUpdateBusiness();
  const [createOpen, setCreateOpen] = useState(false);
  const [editBusiness, setEditBusiness] = useState<Business | null>(null);
  const [archivingBusiness, setArchivingBusiness] = useState<Business | null>(
    null,
  );
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  function showFeedback(type: "success" | "error", message: string) {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 4000);
  }

  async function handleArchiveConfirm() {
    if (!archivingBusiness) return;
    try {
      await updateBusiness.mutateAsync({
        id: archivingBusiness.id,
        status: "inactive",
      });
      showFeedback("success", `"${archivingBusiness.name}" archived.`);
    } catch (err) {
      showFeedback(
        "error",
        err instanceof Error ? err.message : "Failed to archive business",
      );
    }
    setArchivingBusiness(null);
  }

  async function handleReactivate(b: Business) {
    try {
      await updateBusiness.mutateAsync({ id: b.id, status: "active" });
      showFeedback("success", `"${b.name}" reactivated.`);
    } catch (err) {
      showFeedback(
        "error",
        err instanceof Error ? err.message : "Failed to reactivate business",
      );
    }
  }

  const businesses = data?.businesses ?? [];

  return (
    <SettingsPage
      title="Businesses"
      subtitle="Manage the businesses under your account."
      actions={
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" />
          New business
        </Button>
      }
    >
      {feedback && (
        <Alert
          variant={feedback.type === "error" ? "destructive" : "default"}
          className={
            feedback.type === "success"
              ? "mb-4 border-[color:var(--color-success)] bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]"
              : "mb-4"
          }
        >
          <AlertDescription>{feedback.message}</AlertDescription>
        </Alert>
      )}

      <SettingsTable
        colSpan={5}
        isLoading={isLoading}
        isEmpty={businesses.length === 0}
        emptyMessage="No businesses yet. Create one to get started."
      >
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="hidden md:table-cell">Vertical</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="hidden lg:table-cell">Created</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {businesses.map((biz) => (
            <TableRow key={biz.id}>
              <TableCell>
                <p className="font-medium">{biz.name}</p>
                <p className="text-xs text-[color:var(--color-text-md)] font-mono">
                  {biz.slug}
                </p>
              </TableCell>
              <TableCell className="hidden md:table-cell text-sm text-[color:var(--color-text-md)]">
                {biz.vertical ?? "—"}
              </TableCell>
              <TableCell>
                <StatusBadge status={biz.status} />
              </TableCell>
              <TableCell className="hidden lg:table-cell text-xs text-[color:var(--color-text-md)]">
                {formatDate(biz.created_at)}
              </TableCell>
              <TableCell>
                <ActionsMenu
                  business={biz}
                  onEdit={setEditBusiness}
                  onArchive={setArchivingBusiness}
                  onReactivate={handleReactivate}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </SettingsTable>

      <BusinessFormSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      <BusinessFormSheet
        open={!!editBusiness}
        onClose={() => setEditBusiness(null)}
        business={editBusiness}
      />

      <ConfirmDialog
        open={!!archivingBusiness}
        onOpenChange={(open) => !open && setArchivingBusiness(null)}
        title={`Archive "${archivingBusiness?.name}"?`}
        description="This will deactivate the business."
        confirmLabel="Archive"
        onConfirm={handleArchiveConfirm}
      />
    </SettingsPage>
  );
}
